import React, { useState } from 'react';
import ObjectTopBar from '../components/topbar/ObjectTopBar';
import IntegrationsEnabledTable from '../components/integrations/IntegrationsEnabledTable.jsx';
import IntegrationAvailableCard from '../components/integrations/IntegrationAvailableCard.jsx';
import IntegrationDetailSidebar from '../components/integrations/IntegrationDetailSidebar.jsx';
import IntegrationSetupModal from '../components/integrations/IntegrationSetupModal.jsx';
import useDocumentTitle from '../../shared/hooks/useDocumentTitle.js';
import useIntegrationsManagement from '../hooks/useIntegrationsManagement.js';
import LoadingView from '../../shared/components/LoadingView';

export default function OrganizationIntegrationsPage() {
  useDocumentTitle('Integrations – Organization settings – Maintella');

  const {
    enabled,
    available,
    loading,
    error,
    actionLoadingId,
    actionError,
    clearActionError,
    toggleSyncEnabled,
    triggerManualSync,
    selectedIntegrationId,
    setSelectedIntegrationId,
    detail,
    detailLoading,
    updateIntegration,
    createIntegration,
  } = useIntegrationsManagement();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [setupType, setSetupType] = useState(null);
  const [creating, setCreating] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  const openIntegration = (integration) => {
    setSelectedIntegrationId(integration.id);
    setSidebarOpen(true);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const handleToggleSync = async (integration, nextValue, event) => {
    event?.stopPropagation();
    clearActionError();
    try {
      await toggleSyncEnabled(integration, nextValue);
    } catch {
      // error surfaced via actionError
    }
  };

  const handleManualSync = async (integrationId, event) => {
    event?.stopPropagation();
    clearActionError();
    try {
      await triggerManualSync(integrationId);
    } catch {
      // error surfaced via actionError
    }
  };

  const handleSaveSettings = async (patch, credentials) => {
    if (!selectedIntegrationId) return;
    setSavingSettings(true);
    clearActionError();
    try {
      await updateIntegration(selectedIntegrationId, patch, credentials);
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSidebarSync = async () => {
    if (!selectedIntegrationId) return;
    clearActionError();
    try {
      await triggerManualSync(selectedIntegrationId);
    } catch {
      // error surfaced via actionError
    }
  };

  const handleCreateIntegration = async (payload) => {
    setCreating(true);
    clearActionError();
    try {
      const created = await createIntegration(payload);
      setSetupType(null);
      setSelectedIntegrationId(created.id);
      setSidebarOpen(true);
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <ObjectTopBar title="Integrations" />

      <div className="container py-4">
        <div className="mb-4">
          <h6 className="text-muted fw-normal mb-1">External integrations</h6>
          <p className="text-muted small mb-0">
            Connect external systems to import assets automatically. Enable an integration,
            run sync, and linked chargers appear in Maintella without manual entry.
          </p>
        </div>

        {loading && enabled.length === 0 && available.length === 0 ? (
          <LoadingView
            symbolClass="bi-plug"
            title="Loading integrations"
            subtitle="Fetching integration settings for your organization."
            className="py-5"
          />
        ) : (
          <>
            {error && (
              <div className="alert alert-danger mb-3" role="alert">
                <i className="bi bi-exclamation-triangle me-2" />
                {error.message || 'Failed to load integrations.'}
              </div>
            )}

            {actionError && (
              <div className="alert alert-warning alert-dismissible fade show mb-3" role="alert">
                {actionError}
                <button type="button" className="btn-close" aria-label="Close" onClick={clearActionError} />
              </div>
            )}

            <section className="mb-4">
              <h6 className="text-muted fw-normal mb-3">Enabled integrations</h6>
              <IntegrationsEnabledTable
                integrations={enabled}
                actionLoadingId={actionLoadingId}
                selectedIntegrationId={selectedIntegrationId}
                onSelectIntegration={openIntegration}
                onToggleSync={handleToggleSync}
                onManualSync={handleManualSync}
              />
            </section>

            <section>
              <h6 className="text-muted fw-normal mb-3">Available integrations</h6>
              {available.length === 0 ? (
                <p className="text-muted small mb-0">
                  All supported integrations are already enabled for your organization.
                </p>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {available.map((integrationType) => (
                    <IntegrationAvailableCard
                      key={integrationType.type}
                      integrationType={integrationType}
                      onSetup={() => setSetupType(integrationType)}
                    />
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </div>

      <IntegrationDetailSidebar
        show={sidebarOpen}
        detail={detail}
        detailLoading={detailLoading}
        saving={savingSettings}
        syncing={actionLoadingId === selectedIntegrationId}
        onClose={closeSidebar}
        onSave={handleSaveSettings}
        onSync={handleSidebarSync}
      />

      <IntegrationSetupModal
        show={Boolean(setupType)}
        integrationType={setupType}
        saving={creating}
        onClose={() => setSetupType(null)}
        onCreate={handleCreateIntegration}
      />
    </>
  );
}
